import {Button, Checkbox, Typography, Tag, Select, Tooltip, Card } from "antd";
import { isStaffEmail } from "@utils/user_utils";
import "./styles.css";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getBullets, setBullets, updateBullets } from "../../../store/actions/settings.actions";
import { useSelector } from "react-redux";
import { SaveOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import BlurredBulletPicker from "./blurred_bullet_picker.png";
import { PremiumFeatureDisabled } from "../../Common/PremiumFeatureDisabled";
import { audioMessage as message } from "../../Common/Notifications/audioMessage";
import { PermissionedButton, PermissionOverlay } from "../../PermissionedComponents";
import { AdminNotice } from "../AdminNotice";
import { useAuth0 } from "@auth0/auth0-react";
import { isBulletDisabled } from "./isBulletDisabled";

const { Text, Title } = Typography;

const SortableBulletItem = ({
  bullet,
  index,
  bulletDisplaySelections,
  displayOnMultiSelectOptions,
  bulletMetricsSelections,
  metricMultiSelectOptions,
  onBulletCheck,
  onDisplaySectionCheck,
  onMetricSectionCheck,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${bullet.id}`,
  });

  return (
    <Tooltip
      title={bullet.disabled ? "This bullet requires a metric that is not assigned to this team" : null}
      placement="left"
      key={bullet.id}>
      <div
        className={`bullet-container ${bullet.disabled ? "bullet-disabled" : ""}`}
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          cursor: bullet.disabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
        }}
      >
        <div className="title-displayon-container">
          <div style={{height: 'fit-content', textAlign: 'left', width: '65%'}}>
            <Checkbox disabled={bullet.disabled} checked={bullet?.show} onChange={(e) => onBulletCheck(bullet, e.target.checked)}>
              <Title level={5}>{bullet?.name}</Title>
            </Checkbox>
            <div className="bullet-example-text" dangerouslySetInnerHTML={{ __html: bullet?.example }}></div>
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: '5px', width: '34%'}}>
          <Select
            placeholder={<span>Report Page</span>}
            mode="multiple"
            className="option-group-select"
            onChange={(checked) => onDisplaySectionCheck(bullet, index, checked)}
            value={bulletDisplaySelections[index]}
            style={{ width: '100%%' }}
            options={displayOnMultiSelectOptions}
            maxTagCount={2}
          >
          </Select>
          {
            (
              bullet?.bullet_metadata?.show_metric_dropdown &&
              bulletMetricsSelections[index] &&
              metricMultiSelectOptions
            ) && (
                <Select
                  placeholder={<span>Select Metric(s)</span>}
                  mode="multiple"
                  className="option-group-select"
                  onChange={(checked) => onMetricSectionCheck(bullet, index, checked)}
                  value={bulletMetricsSelections[index]}
                  style={{ width: '100%%' }}
                  options={metricMultiSelectOptions}
                  maxTagCount={1}
                >
                </Select>
            )
          }
          </div>
        </div>
      </div>
    </Tooltip>
  );
};

export const ReportBulletPicker = () => {
  const dispatch = useDispatch();
  const [bulletList, setBulletList] = useState(null);
  const [displayOnMultiSelectOptions, setDisplayOnMultiSelectOptions] = useState(null);
  const [metricMultiSelectOptions, setMetricMultiSelectOptions] = useState(null);
  const [bulletDisplaySelections, setBulletDisplaySelections] = useState(null);
  const [bulletMetricsSelections, setBulletMetricsSelections] = useState(null);

  const { user } = useAuth0();
  const isStaff = isStaffEmail(user?.email);

  const {
      positions,
      all_metric_types,
      info: { display_name: teamName, bullet_picker_enabled},
      sport,
    } = useSelector(({ team }) => team);

  const {
      loading: { teamSettings: loading },
      bullets,
      error
    } = useSelector(({ settings }) => settings);

  useEffect(() => {
    dispatch(getBullets());
  }, []);

  useEffect(() => {
    if (bullets && bullets.length > 0) {
      const localBulletCopy = structuredClone(bullets);
      if (all_metric_types && all_metric_types?.gps) {
        const gps_metric_keys = all_metric_types.gps.map(metric => metric.key);

        const final_bullets = localBulletCopy.map(bullet => {
          bullet.disabled = isBulletDisabled(bullet, gps_metric_keys);
          return bullet;
        })
        setBulletList(final_bullets);
      }

      const bulletSelections = localBulletCopy.map(bullet => {
        const metadata = bullet.metadata;
        if (metadata) {
          let selectionValues = [];
          if (metadata?.overview) {
            selectionValues.push("overview");
          }
          if (metadata?.player) {
            selectionValues.push("player");
          }
          if (metadata?.position && metadata?.position["show"]) {
            if (metadata?.position?.position_list && metadata?.position?.position_list.length > 0) {
              selectionValues = [...selectionValues, ...metadata?.position?.position_list]
            } else {
              selectionValues.push("position");
            }
          }
          return selectionValues;
        }
      })
      setBulletDisplaySelections(bulletSelections);

      const bulletMetricSelections = localBulletCopy.map(bullet => {
        const metadata = bullet.metadata;
        if (metadata) {
          let selectionValues = [];
          if (Object.keys(metadata).includes("metric_keys") && metadata?.metric_keys.length > 0) {
            selectionValues.push(...metadata?.metric_keys);
          }

          return selectionValues;
        }
      })

      setBulletMetricsSelections(bulletMetricSelections);

    }

    if (sport) {
      if (sport === "football" && positions) {
        const positionalOptions = positions.map(pos => {
          return {
            label: <span>{pos}</span>,
            value: pos,
          }
        })
        let multiSelectOptions = [
          {
            label: <span>Please select where you want this bullet to displayed</span>,
            title: '',
            options: [
              {
                label: <span>Overview Page</span>,
                value: 'overview',
              },
              {
                label: <span>All Position Pages</span>,
                value: 'position',
              },
            ],
          },
          {
            label: <span>Specific Positions</span>,
            title: 'position',
            options: positionalOptions,
          },
        ]
        setDisplayOnMultiSelectOptions(multiSelectOptions);
      }

      if (sport !== "football") {
        let multiSelectOptions = [
          {
            label: <span>Please select where you want this bullet to displayed</span>,
            title: '',
            options: [
              {
                label: <span>Overview Page</span>,
                value: 'overview',
              },
              {
                label: <span>Player Page</span>,
                value: 'player',
              },
            ],
          },
        ]
        setDisplayOnMultiSelectOptions(multiSelectOptions);
      }
    }

    let metricMultiSelectOptions = (
      all_metric_types?.gps.length > 0
      ?
      (
        all_metric_types?.gps.map(metric => {
          return {
            label: <span>{metric.name}</span>,
            value: metric.key,
          }
        })
      ) : []
    );
    setMetricMultiSelectOptions(metricMultiSelectOptions);

  }, [bullets, sport, positions, all_metric_types]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Function to update list on drop
  const handleDrop = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = bulletList.findIndex((bullet) => `${bullet.id}` === active.id);
    const newIndex = bulletList.findIndex((bullet) => `${bullet.id}` === over.id);
    const updatedList = arrayMove(bulletList, oldIndex, newIndex).map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    const bulletSelections = updatedList.map(bullet => {
      const metadata = bullet.metadata;
      if (metadata) {
        let selectionValues = [];
        if (metadata?.overview) {
          selectionValues.push("overview");
        }
        if (metadata?.player) {
          selectionValues.push("player");
        }
        if (metadata?.position && metadata?.position["show"]) {
          if (metadata?.position?.position_list && metadata?.position?.position_list.length > 0) {
            selectionValues = [...selectionValues, ...metadata?.position?.position_list]
          } else {
            selectionValues.push("position");
          }
        }
        return selectionValues;
      }
    })
    setBulletDisplaySelections(bulletSelections);

    // Update State
    setBulletList(updatedList);
  };

  const handleBulletCheck = (checkedBullet, checked) => {
    let updateIdx;
    const updatedBullets = bulletList.map((bullet, index) => {
      if (bullet.key === checkedBullet?.key) {
        bullet.show = checked;
        bullet.metadata = {};
        updateIdx = index;
      }
      return bullet;
    });

    setBulletDisplaySelections(bulletDisplaySelections.map((selection, idx) => {
      if (idx === updateIdx) {
        return [];
      }

      return selection;
    }))

    setBulletMetricsSelections(bulletMetricsSelections.map((selection, idx) => {
      if (idx === updateIdx) {
        return [];
      }

      return selection;
    }))

    setBulletList(updatedBullets);
  }

  const handleDisplaySectionCheck = (checkedParentBullet, bulletIdx, checked) => {
    let oldSelection = bulletDisplaySelections[bulletIdx];
    let newSelection = [];
    if (oldSelection.includes("position")) {
      const posValues = checked.filter(val => val !== "overview" && val !== "position" && val !== "player")
      if (posValues && posValues.length > 0) {
        setBulletDisplaySelections(
          bulletDisplaySelections.map((selection, index) => {
            if (index === bulletIdx) {
              newSelection = checked.filter(s => s !== "position");
              return newSelection;
            }
            return selection;
          })
        );
      } else {
        setBulletDisplaySelections(
          bulletDisplaySelections.map((selection, index) => {
            if (index === bulletIdx) {
              newSelection = checked;
              return newSelection;
            }
            return selection;
          })
        );
      }
    } else {
      const posValues = oldSelection.filter(val => val !== "overview" && val !== "player")
      if (posValues && posValues.length > 0 && checked.includes("position")) {
        setBulletDisplaySelections(
          bulletDisplaySelections.map((selection, index) => {
            if (index === bulletIdx) {
              newSelection = checked.filter(s => !posValues.includes(s));
              return newSelection
            }
            return selection;
          })
        );
      } else {
        setBulletDisplaySelections(
          bulletDisplaySelections.map((selection, index) => {
            if (index === bulletIdx) {
              newSelection = checked;
              return newSelection;
            }
            return selection;
          })
        );
      }
    }

    const updatedBullets = bulletList.map((bullet) => {
      if (bullet.key === checkedParentBullet?.key) {
        if (newSelection.length > 0) {
          bullet.show = true;
        } else {
          bullet.show = false;
        }
        let newMeta = {}
        if (newSelection.includes('position')) {
          newMeta = {...newMeta, position: { show: true }};
        }
        if (newSelection.includes('overview')){
          newMeta = {...newMeta, overview: true };
        }
        if (newSelection.includes('player')){
          newMeta = {...newMeta, player: true };
        }
        let posList = newSelection.filter(val => val !== "overview" && val !== "position" && val !== "player");
        if (posList && posList.length > 0) {
          newMeta = {...newMeta, position: { show: true, position_list: posList }};
        }

        bullet.metadata = newMeta;

      }
      return bullet;
    });

    setBulletList(updatedBullets);
  }

  const handleMetricSectionCheck = (checkedParentBullet, bulletIdx, checked) => {
    const MAX_METRICS_ALLOWED = 4;
    if (checked.length > MAX_METRICS_ALLOWED) {
      const bullet = bulletList.filter(bullet => bullet.key === checkedParentBullet?.key)
      message.error(`You can only select up to 4 metrics for bullet: ${bullet[0].name}`)
      return
    }

    let newSelection = [...bulletMetricsSelections];
    newSelection[bulletIdx] = [...checked];
    setBulletMetricsSelections([...newSelection]);

    const updatedBullets = bulletList.map((bullet) => {
      if (bullet.key === checkedParentBullet?.key) {
        if (newSelection[bulletIdx].length > 0) {
          bullet.show = true;
        } else {
          if (bulletDisplaySelections[bulletIdx].length > 0){
            bullet.show = true;
          } else {
            bullet.show = false;
          }
        }

        bullet.metadata = {...bullet.metadata, metric_keys: newSelection[bulletIdx] };
      }
      return bullet;
    });

    setBulletList(updatedBullets);
  }

  const handleSaveChanges = () => {
    let bulletsNames = []
    const updatedBullets = structuredClone(bulletList);
    const bulletsWithBlankMeta = updatedBullets.filter(bullet => {
      let metadata = bullet.metadata;
      let invalidBulletChecks = (metadata && typeof metadata === 'object' && Object.keys(metadata).length === 0);
      if (bullet.show) {
        if (invalidBulletChecks){
          bulletsNames.push(bullet.name);
          return bullet;
        }

        const metaKeys = Object.keys(metadata);
        if (
          Object.keys(bullet?.bullet_metadata || {}).includes("show_metric_dropdown") &&
          bullet?.bullet_metadata.show_metric_dropdown &&
          (!metaKeys.includes("metric_keys") || (metaKeys.includes("metric_keys") && metadata["metric_keys"].length <= 0) ||
          (
            (
              sport == "football" &&
              (!metaKeys.includes("overview") || (metaKeys.includes("overview") && !metadata["overview"])) &&
              (!metaKeys.includes("position") || (metaKeys.includes("position") && !metadata["position"]["show"]))
            ) ||
            (
              sport != "football" &&
              (!metaKeys.includes("overview") || (metaKeys.includes("overview") && !metadata["overview"])) &&
              (!metaKeys.includes("player") || (metaKeys.includes("player") && !metadata["player"]))
            )
          )
        ))  {
          bulletsNames.push(bullet.name);
          return bullet;
        }

        if (sport == "football") {
          if(
              metaKeys.length == 3 &&
              (
                (metaKeys.includes("overview") && !metadata["overview"]) &&
                (metaKeys.includes("position") && !metadata["position"]["show"]) &&
                (metaKeys.includes("metric_keys") && metadata["metric_keys"].length <= 0)
              )
            ) {
                bulletsNames.push(bullet.name);
                return bullet;
          }

          if(
              metaKeys.length == 2 &&
              (
                (
                  (metaKeys.includes("overview") && !metadata["overview"]) &&
                  (metaKeys.includes("position") && !metadata["position"]["show"])
                ) || (
                  (metaKeys.includes("overview") && !metadata["overview"]) &&
                  (metaKeys.includes("metric_keys") && metadata["metric_keys"].length <= 0)
                ) || (
                  (metaKeys.includes("position") && !metadata["position"]["show"]) &&
                  (metaKeys.includes("metric_keys") && metadata["metric_keys"].length <= 0)
                )
              )
            ) {
                bulletsNames.push(bullet.name);
                return bullet;
          }

          if(
              metaKeys.length == 1 &&
              (
                (metaKeys.includes("overview") && !metadata["overview"]) ||
                (metaKeys.includes("position") && !metadata["position"]["show"]) ||
                (metaKeys.includes("metric_keys") && metadata["metric_keys"].length <= 0)
              )
           ) {
                bulletsNames.push(bullet.name);
                return bullet;
          }
        } else {
          if(
              metaKeys.length == 3 &&
              (
                (metaKeys.includes("overview") && !metadata["overview"]) &&
                (metaKeys.includes("player") && !metadata["player"]) &&
                (metaKeys.includes("metric_keys") && metadata["metric_keys"].length <= 0)
              )
           ) {
              bulletsNames.push(bullet.name);
              return bullet;
          }

          if(
              metaKeys.length == 2 &&
              (
                (
                  (metaKeys.includes("overview") && !metadata["overview"]) &&
                  (metaKeys.includes("player") && !metadata["player"])
                ) || (
                  (metaKeys.includes("overview") && !metadata["overview"]) &&
                  (metaKeys.includes("metric_keys") && metadata["metric_keys"].length <= 0)
                ) || (
                  (metaKeys.includes("player") && !metadata["player"]) &&
                  (metaKeys.includes("metric_keys") && metadata["metric_keys"].length <= 0)
                )
              )
           ) {
              bulletsNames.push(bullet.name);
              return bullet;
          }

          if(
              metaKeys.length == 1 &&
              (
                (metaKeys.includes("overview") && !metadata["overview"]) ||
                (metaKeys.includes("player") && !metadata["player"]) ||
                (metaKeys.includes("metric_keys") && metadata["metric_keys"].length <= 0)
              )
           ) {
              bulletsNames.push(bullet.name);
              return bullet;
          }
        }
      }
    })

    if (bulletsWithBlankMeta && bulletsWithBlankMeta.length > 0) {
      message.error(`Please make valid selections for bullets: ${bulletsNames.join(', ')}`)
      return
    }

    const originalBulletsByKey = Object.fromEntries(bullets.map(i => [i.key, i]));
    const bullet_list = updatedBullets.filter((bullet) => {
      const original_bullet = originalBulletsByKey[bullet.key];
      let new_bullet = {};
      if (original_bullet["show"] != bullet["show"]) {
        new_bullet.id = bullet.id;
        new_bullet.show = bullet.show;
        new_bullet.metadata = bullet.metadata;
      }

      if (original_bullet["order"] && original_bullet["order"] != bullet["order"]) {
        new_bullet.id = bullet.id;
        new_bullet.order = bullet.order;
      }

      if (JSON.stringify(original_bullet["metadata"]) != JSON.stringify(bullet["metadata"])) {
        new_bullet.id = bullet.id;
        new_bullet.metadata = bullet.metadata;
      }

      if (Object.keys(new_bullet).length > 0)
        return new_bullet;

    });

    let final_bullets = bullet_list.map(b => {
      
      return {
        id: b.id,
        metadata: b.metadata,
        order: b.order,
        show: b.show
      }
    });

    if (final_bullets.length === 0) {
      message.info("No changes to save");
      return;
    }

    dispatch(updateBullets(final_bullets));
    dispatch(setBullets(updatedBullets));
  }

  if (!bullet_picker_enabled && !isStaff) {
    return (
      <PremiumFeatureDisabled
        bg_image={BlurredBulletPicker}
        show_navbar={false}
        feature_name={"Pick Your Bullets"}
        info_text={<>
        This is a premium feature that will allow you to customize which insight bullets you see on your GPS reports. <br></br>
        If you are interested in adding this to your platform, please contact your sales rep!</>}
      />
    )
  }

  return (
    <div className="bullets-container">
      {(!bullet_picker_enabled && isStaff) && (
        <AdminNotice style={{ marginBottom: '16px' }} />
      )}
      <PermissionedButton
        icon={<SaveOutlined />}
        permissionCheck="EditReportBullets"
        tooltipMessage="You don't have permission to edit report bullets"
        type="primary"
        onClick={handleSaveChanges}
      >
        Save Changes
      </PermissionedButton>
      <PermissionOverlay
        permissionCheck="EditReportBullets"
        tooltipMessage="You don't have permission to edit report bullets"
      >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDrop}
      >
        <SortableContext
          items={bulletList?.map((bullet) => `${bullet.id}`) ?? []}
          strategy={verticalListSortingStrategy}
        >
          <div className="list-container">
            {bulletList?.map((bullet, index) => (
              <SortableBulletItem
                key={bullet.id}
                bullet={bullet}
                index={index}
                bulletDisplaySelections={bulletDisplaySelections}
                displayOnMultiSelectOptions={displayOnMultiSelectOptions}
                bulletMetricsSelections={bulletMetricsSelections}
                metricMultiSelectOptions={metricMultiSelectOptions}
                onBulletCheck={handleBulletCheck}
                onDisplaySectionCheck={handleDisplaySectionCheck}
                onMetricSectionCheck={handleMetricSectionCheck}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      </PermissionOverlay>
    </div>
  );
};
